/**
 * Listenstandard (WARTESCHLANGE Punkt 95, `docs/PLAN-listenstandard.md`, Brief
 * `docs/design/listenstandard/README.md` § 3): Sortierung für jede Liste.
 *
 * - `sortiereZeilen` ist rein und ohne React testbar.
 * - `useListSort` hält {key, dir}: URL (`?sort=…&dir=asc|desc`) vor localStorage
 *   (`maestro:list:{listId}:sort`). Andere Suchparameter (z. B. `filter`) bleiben unberührt.
 *
 * Alle Listen liegen komplett im Client (keine API-Paginierung, Plan § Grundbefund) — sortiert
 * wird deshalb hier, die API bleibt unverändert.
 */
import { useSearchParams } from "react-router-dom";

export type SortTyp = "text" | "date" | "number" | "money" | "status";
export type SortRichtung = "asc" | "desc";
type Wert = string | number | null | undefined;

export interface SortOptionen<T> {
  accessor: (row: T) => Wert;
  type: SortTyp;
  dir: SortRichtung;
  /** Nur für `type: "status"`: fachliche Reihenfolge (z. B. `BUCHUNG_STATUS_REIHENFOLGE`). */
  statusOrder?: Readonly<Record<string, number>>;
}

/** Sortierschlüssel oder `null` für einen Leerwert (null/undefined/""/ungültiges Datum). */
function schluessel(v: Wert, type: SortTyp, statusOrder?: Readonly<Record<string, number>>): string | number | null {
  if (v == null || v === "") return null;
  if (type === "text") return String(v);
  // Unbekannter Status: hinter alle bekannten, aber vor die Leerwerte.
  if (type === "status") return statusOrder?.[String(v)] ?? Number.MAX_SAFE_INTEGER;
  const n = type === "date" && typeof v === "string" ? Date.parse(v) : Number(v);
  return Number.isNaN(n) ? null : n;
}

/**
 * Sortiert eine Kopie von `rows`. Leerwerte stehen IMMER am Ende (auf- wie absteigend), Text
 * vergleicht deutsch ohne Groß/Klein- und Akzentunterschied (Ä neben A, nicht hinter Z), bei
 * Gleichstand bleibt die Ausgangsreihenfolge (stabil).
 */
export function sortiereZeilen<T>(rows: readonly T[], { accessor, type, dir, statusOrder }: SortOptionen<T>): T[] {
  const faktor = dir === "desc" ? -1 : 1;
  return rows
    .map((row, i) => ({ row, i, k: schluessel(accessor(row), type, statusOrder) }))
    .sort((a, b) => {
      if (a.k === null || b.k === null) return a.k === b.k ? a.i - b.i : a.k === null ? 1 : -1;
      const c =
        type === "text"
          ? String(a.k).localeCompare(String(b.k), "de", { sensitivity: "base" })
          : (a.k as number) - (b.k as number);
      return c * faktor || a.i - b.i;
    })
    .map((x) => x.row);
}

export interface SortSpalte<T> {
  key: string;
  label: string;
  type: SortTyp;
  accessor: (row: T) => Wert;
  statusOrder?: Readonly<Record<string, number>>;
}

export interface ListSortZustand<T> {
  sortKey: string | null;
  dir: SortRichtung | null;
  /** Aktive Spalte (für Fußzeile/Hinweis), sonst `null`. */
  spalte: SortSpalte<T> | null;
  /** Zyklus je Spalte: auf → ab → aus; eine andere Spalte startet aufsteigend. */
  toggle: (key: string) => void;
  /** Löscht URL-Parameter und localStorage (= Standardreihenfolge). */
  reset: () => void;
  /** Setzt die Sortierung direkt (Sortiermenü statt Spaltenkopf-Zyklus), `null` = Standard. */
  setze: (wert: { key: string; dir: SortRichtung } | null) => void;
  /** Sortiert nach dem aktuellen Zustand; ohne Sortierung unverändert. */
  sortiere: (rows: readonly T[]) => T[];
}

const speicherKey = (listId: string) => `maestro:list:${listId}:sort`;

function ausSpeicher(listId: string): { key?: unknown; dir?: unknown } | null {
  try {
    return JSON.parse(window.localStorage.getItem(speicherKey(listId)) ?? "null");
  } catch {
    return null;
  }
}

function inSpeicher(listId: string, wert: { key: string; dir: SortRichtung } | null) {
  try {
    if (wert) window.localStorage.setItem(speicherKey(listId), JSON.stringify(wert));
    else window.localStorage.removeItem(speicherKey(listId));
  } catch {
    // Privates Fenster/blockierter Speicher: die URL trägt den Zustand trotzdem.
  }
}

const istRichtung = (d: unknown): d is SortRichtung => d === "asc" || d === "desc";

export function useListSort<T>(listId: string, columns: readonly SortSpalte<T>[]): ListSortZustand<T> {
  const [params, setParams] = useSearchParams();
  const finde = (key: unknown) => columns.find((c) => c.key === key) ?? null;

  const urlKey = params.get("sort");
  const urlDir = params.get("dir");
  let spalte: SortSpalte<T> | null = null;
  let dir: SortRichtung | null = null;
  if (finde(urlKey) && istRichtung(urlDir)) {
    spalte = finde(urlKey);
    dir = urlDir;
  } else {
    const gespeichert = ausSpeicher(listId);
    if (gespeichert && finde(gespeichert.key) && istRichtung(gespeichert.dir)) {
      spalte = finde(gespeichert.key);
      dir = gespeichert.dir;
    }
  }

  function setze(wert: { key: string; dir: SortRichtung } | null) {
    inSpeicher(listId, wert);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (wert) {
          next.set("sort", wert.key);
          next.set("dir", wert.dir);
        } else {
          next.delete("sort");
          next.delete("dir");
        }
        return next;
      },
      { replace: true },
    );
  }

  return {
    sortKey: spalte?.key ?? null,
    dir,
    spalte,
    toggle(key) {
      if (spalte?.key !== key) setze({ key, dir: "asc" });
      else if (dir === "asc") setze({ key, dir: "desc" });
      else setze(null);
    },
    reset: () => setze(null),
    setze,
    sortiere: (rows) =>
      spalte && dir
        ? sortiereZeilen(rows, { accessor: spalte.accessor, type: spalte.type, dir, statusOrder: spalte.statusOrder })
        : [...rows],
  };
}
